import React, { useContext, useState } from "react";
import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { MIME_TYPE } from "common/constants";
import { getPath } from "common/routing";
import { useDispatchBulkGeocodeCsvScriptJobMutation } from "../../../../generated/graphql";
import { GeocodeAccuracyProfile } from "../../../../generated/graphql";
import { useMultipartFileUpload } from "../../../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { AuthContext } from "../../../Authorization/AuthContext";
import { SectionDivider } from "../../../Common/__styles__/FullPageFormLayout";
import { Button } from "../../../Common/Button";
import FullPageFormLayout from "../../../Common/FullPageFormLayout";
import { Body, Title } from "../../../Common/Typography";
import { Number, Select } from "../../../Inputs/react-hook-form";
import { Section } from "../../__styles__/Content";
import {
  FormTitleWrapper,
  InputWrapper,
  List,
  ListItem,
} from "./__styles__/Operations";
import { InputFile } from "./shared";

type DispatchBulkGeocodeFormData = {
  inputCsv: { blob: File };
  accuracyProfile: GeocodeAccuracyProfile;
  accuracyThreshold: number;
};

const Content = ({ children }: { children: React.ReactNode }) => (
  <Body size="default" type="regular" color="contentSecondary" as="div">
    {children}
  </Body>
);

const DispatchBulkGeocodeForm = () => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const formMethods = useForm<DispatchBulkGeocodeFormData>({
    defaultValues: {
      accuracyProfile: GeocodeAccuracyProfile.EXCLUDE_RANGE_INTERPOLATION,
      accuracyThreshold: 0.9,
    },
  });
  const { control, getValues, handleSubmit, register, watch } = formMethods;
  const [hasDispatchedOperation, setHasDispatchedOperation] = useState(false);

  const [uploadSourceFile, { loading: isUploading }] = useMultipartFileUpload({
    onError: () => addErrorToast("Failed to upload input file!"),
  });

  const [dispatch, { loading: isDispatching }] =
    useDispatchBulkGeocodeCsvScriptJobMutation({
      onCompleted: () => {
        addSuccessToast("Dispatched bulk geocode operation!");
        setHasDispatchedOperation(true);
      },
      onError: () => {
        addErrorToast("Failed to dispatch bulk geocode operation!");
      },
    });

  const onSubmit = async () => {
    const formValues = getValues();
    uploadSourceFile({
      file: formValues.inputCsv.blob,
      onCompleted: async key => {
        await dispatch({
          variables: {
            data: {
              accountId: account!.id,
              inputFileKey: key,
              accuracyProfile: formValues.accuracyProfile,
              accuracyThreshold: formValues.accuracyThreshold,
            },
          },
        });
      },
    });
  };

  const fileUploadValue = watch("inputCsv");
  const accuracyProfileValue = watch("accuracyProfile");
  const accuracyThresholdValue = watch("accuracyThreshold");
  const missingRequiredField =
    isEmpty(fileUploadValue) ||
    isEmpty(accuracyProfileValue) ||
    accuracyThresholdValue === null ||
    accuracyThresholdValue === undefined;
  const isSubmitting = isUploading || isDispatching;
  const submitIsDisabled =
    isSubmitting || missingRequiredField || hasDispatchedOperation;

  return (
    <FullPageFormLayout
      subtitle="Bulk geocode addresses"
      prevLocation={getPath("operations")}
      rightContainer={
        <Button
          size={"small"}
          styleVariant={"primary"}
          onClick={handleSubmit(onSubmit)}
          disabled={submitIsDisabled}
          loading={isSubmitting}
        >
          Create
        </Button>
      }
    >
      <FormProvider {...formMethods}>
        <form>
          <Section>
            <FormTitleWrapper>
              <Title size="large" type="semiBold">
                Bulk address geocoding
              </Title>
            </FormTitleWrapper>
            <Content>
              This form will allow you to upload a CSV file containing address
              information and dispatch a script to bulk geocode it using
              Geocodio.
              <br />
              <br />
              When the script is complete you will receive an email with a link
              to download a CSV containing any errors that occurred during the
              operation.
            </Content>
          </Section>
          <SectionDivider />
          <Section>
            <FormTitleWrapper>
              <Title size={"small"} type={"regular"}>
                Address columns
              </Title>
            </FormTitleWrapper>
            <List>
              <ListItem>
                <Content>
                  The first row of your CSV should contain the column headers.
                </Content>
              </ListItem>
              <ListItem>
                <Content>
                  There must be address columns and they must come in at least
                  one of the following combinations:
                </Content>
                <pre>fullAddress</pre>
                <Body type="emphasis" size="default" color="contentSecondary">
                  OR
                </Body>
                <pre>streetAddress, city, state, zip</pre>
                <Body type="emphasis" size="default" color="contentSecondary">
                  OR
                </Body>
                <pre>streetNumber, streetName, suffix, city, state, zip</pre>
              </ListItem>
            </List>
          </Section>
          <Section>
            <FormTitleWrapper>
              <Title size={"small"} type={"regular"}>
                Output columns
              </Title>
            </FormTitleWrapper>
            <Content>
              The output CSV will have the following columns appended:
              <pre>
                geocodedFullAddress, geocodedStreetAddress, geocodedCity,
              </pre>
              <pre>
                geocodedState, geocodedZip, geocodedLatitude, geocodedLongitude,
              </pre>
              <pre>geocodedAccuracy, geocodedAccuracyType, message</pre>
            </Content>
          </Section>
          <SectionDivider />
          <Section>
            <FormTitleWrapper>
              <Title size={"small"} type={"regular"}>
                Geocoding Configuration
              </Title>
            </FormTitleWrapper>
            <Content>
              The accuracy profile controls the minimum accuracy requirement for
              geocoding results.
              <List>
                <ListItem>
                  <Content>
                    Exclude Range Interpolation: This is the default and
                    recommended option. It will exclude results that have a
                    range interpolation accuracy score.
                  </Content>
                </ListItem>
                <ListItem>
                  <Content>
                    Rooftop Only: This will only include results that have a
                    rooftop accuracy score.
                  </Content>
                </ListItem>
                <ListItem>
                  <Content>
                    Any: This will include all results, regardless of accuracy
                    score.
                  </Content>
                </ListItem>
              </List>
              <Content>
                The accuracy threshold controls the minimum accuracy score (0.0
                to 1.0 where 1.0 means 100% accuracy) required for geocoding
                results.
              </Content>
              <List>
                <ListItem>
                  <Content>
                    0.9 is the default and recommended option and is the value
                    used for in-app geocoding. It will exclude results that have
                    an accuracy score less than your chosen threshold.
                  </Content>
                </ListItem>
              </List>
            </Content>
            <br />
            <InputWrapper>
              <Select
                control={control}
                name="accuracyProfile"
                label="Accuracy Profile"
                options={[
                  {
                    label: "Exclude Range Interpolation (Recommended)",
                    value: GeocodeAccuracyProfile.EXCLUDE_RANGE_INTERPOLATION,
                  },
                  {
                    label: "Rooftop Only",
                    value: GeocodeAccuracyProfile.ROOFTOP,
                  },
                  {
                    label: "Any Accuracy",
                    value: GeocodeAccuracyProfile.ANY,
                  },
                ]}
                required
              />
            </InputWrapper>
            <InputWrapper>
              <Number
                rules={{
                  valueAsNumber: true,
                  min: 0,
                  max: 1,
                }}
                register={register}
                name="accuracyThreshold"
                label="Accuracy Threshold"
                description="Minimum accuracy score (0.0 to 1.0) required for geocoding results"
                step={0.05}
                required
              />
            </InputWrapper>
          </Section>
          <SectionDivider />
          <Section>
            <InputFile
              name="inputCsv"
              allowedMimeTypes={[MIME_TYPE.CSV]}
              label="Source file"
              description="The csv file with the addresses to geocode"
              required
            />
          </Section>
        </form>
      </FormProvider>
    </FullPageFormLayout>
  );
};

export default DispatchBulkGeocodeForm;
